import {
  FollowerMembershipState,
  FollowerStatus,
  FollowerSubscription,
  MembershipPlan,
} from "@markit/common.types";
import {
  getUserFollowersRef,
  getUserFollowerSubscriptionsRef,
  getUserMembershipPromoCodesSnap,
} from "./FirebaseUtils";
import { orderBy, limit, query, getDocs, where } from "../firebase";
import { LessThanDate } from "@markit/common.utils";

// Gets the most recent follower subscription for a given creator and follower
export const getMostRecentFollowerSubscription = async (
  creatorUid: string,
  followerUid: string
): Promise<FollowerSubscription | undefined> => {
  const followerSubscriptionsRef = getUserFollowerSubscriptionsRef(
    creatorUid,
    followerUid
  );
  const query_ = query(
    followerSubscriptionsRef,
    orderBy("end", "desc"),
    limit(1)
  );
  const snapshot = await getDocs(query_);
  if (!snapshot.empty) {
    const latestFollowerSubscription = snapshot.docs[0].data();
    return latestFollowerSubscription;
  }
  return undefined;
};

export const getFollowerSubscriptions = async (
  creatorUid: string,
  followerId: string
) => {
  const followerSubscriptionsRef = getUserFollowerSubscriptionsRef(
    creatorUid,
    followerId
  );
  const followerSubscriptionsSnap = await getDocs(
    query(followerSubscriptionsRef, orderBy("end", "desc"))
  );
  return followerSubscriptionsSnap.docs.map((doc) => doc.data());
};

// Gets the monthly revenue from all active follower subscriptions
export const getFollowerSubscriptionsMonthlyRevenue = async (
  userId: string,
  membershipPlans: MembershipPlan[]
) => {
  const followersRef = getUserFollowersRef(userId);

  // Retrieve membership promo codes and active subscribed membership followers
  const [membershipPromoCodesSnap, activeFollowersQuery] = await Promise.all([
    getUserMembershipPromoCodesSnap(userId),
    query(
      followersRef,
      where("status", "==", FollowerStatus.SUBSCRIBED),
      where("membershipState", "==", FollowerMembershipState.ACTIVE)
    ),
  ]);
  const membershipPromoCodes = membershipPromoCodesSnap.docs.map((doc) =>
    doc.data()
  );

  const snapshot = await getDocs(activeFollowersQuery);
  const totalRevenue = snapshot.docs.reduce((acc, doc) => {
    const followerData = doc.data();
    const foundPromoCode = membershipPromoCodes.find(
      (code) => code.id === followerData.membershipPromoCodeId
    );

    const membershipPlan = membershipPlans.find(
      (plan) => plan.id === followerData.membershipPlanId
    );
    // Check if discount end date has passed or doesn't exist
    const beforeDiscountEndDate =
      !followerData.discountEndDate ||
      (followerData.discountEndDate &&
        LessThanDate(new Date().toISOString(), followerData.discountEndDate));
    const planPrice = membershipPlan
      ? foundPromoCode && beforeDiscountEndDate
        ? membershipPlan.price * (1 - foundPromoCode.value / 100)
        : membershipPlan.price
      : 0;
    return acc + planPrice;
  }, 0);
  return totalRevenue / 100;
};
